import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Description as DocumentsIcon,
  Storage as DatabaseIcon,
  Psychology as AIIcon,
  Chat as ChatbotIcon,
  ExpandLess,
  ExpandMore,
  SettingsApplications as GlobalSettingsIcon,
  Category as DocumentTypeIcon,
  CloudUpload as VectorDocumentsIcon,
  Schema as SchemaIcon,
  TableChart as TableIcon,
  SmartToy as AgentIcon,
  AccountTree as NodeIcon,
  Hub as GraphIcon,
  History as HistoryIcon,
  QuestionAnswer as ClarificationIcon,
  PlaylistPlay as OptionQueryIcon,
  ViewModule as ChatbotSectionIcon,
  AccountTreeOutlined as DecisionTreeIcon,
  Forum as GuidedChatbotIcon,
  AutoFixHigh as ReactChatbotIcon,
} from '@mui/icons-material';
import './Sidebar.css';

const DRAWER_WIDTH = 260;

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  subItems?: SubMenuItem[];
}

interface SubMenuItem {
  id: string;
  label: string;
  path: string;
  icon?: React.ReactNode;
}

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems: MenuItem[] = [
    {
      id: 'configuraciones',
      label: 'Configuraciones',
      icon: <SettingsIcon />,
      subItems: [
        {
          id: 'global-settings',
          label: 'Configuraciones Globales',
          path: '/global-settings',
          icon: <GlobalSettingsIcon fontSize="small" />,
        },
      ],
    },
    {
      id: 'documentos',
      label: 'Documentos',
      icon: <DocumentsIcon />,
      subItems: [
        {
          id: 'document-types',
          label: 'Tipo de Documentos',
          path: '/document-types',
          icon: <DocumentTypeIcon fontSize="small" />,
        },
        {
          id: 'documents-vectorized',
          label: 'Documentos Vectorizados',
          path: '/documents-vectorized',
          icon: <VectorDocumentsIcon fontSize="small" />,
        },
      ],
    },
    {
      id: 'estructura-db',
      label: 'Estructura DB',
      icon: <DatabaseIcon />,
      subItems: [
        {
          id: 'schemas',
          label: 'Esquemas',
          path: '/schemas',
          icon: <SchemaIcon fontSize="small" />,
        },
        {
          id: 'tables',
          label: 'Tablas',
          path: '/tables',
          icon: <TableIcon fontSize="small" />,
        },
        {
          id: 'nodes',
          label: 'Nodos',
          path: '/nodes',
          icon: <NodeIcon fontSize="small" />,
        },
        {
          id: 'graphs',
          label: 'Grafos',
          path: '/graphs',
          icon: <GraphIcon fontSize="small" />,
        },
        {
          id: 'option-queries',
          label: 'Query Opciones',
          path: '/option-queries',
          icon: <OptionQueryIcon fontSize="small" />,
        },
        {
          id: 'chatbot-sections',
          label: 'Secciones Chatbot',
          path: '/chatbot-sections',
          icon: <ChatbotSectionIcon fontSize="small" />,
        },
        {
          id: 'query-history',
          label: 'Historial Queries',
          path: '/query-history',
          icon: <HistoryIcon fontSize="small" />,
        },
      ],
    },
    {
      id: 'ia-llm',
      label: 'IA y LLM',
      icon: <AIIcon />,
      subItems: [
        {
          id: 'agent-configurations',
          label: 'Agentes',
          path: '/agent-configurations',
          icon: <AgentIcon fontSize="small" />,
        },
        {
          id: 'clarification-schemas',
          label: 'Clarificación',
          path: '/clarification-schemas',
          icon: <ClarificationIcon fontSize="small" />,
        },
        {
          id: 'decision-trees',
          label: 'Árbol de Decisiones',
          path: '/decision-trees',
          icon: <DecisionTreeIcon fontSize="small" />,
        },
      ],
    },
    // {
    //   id: 'chatbot',
    //   label: 'Chatbot',
    //   icon: <ChatbotIcon />,
    //   path: '/chatbot',
    // },
    {
      id: 'react-chatbot',
      label: 'Chatbot',
      // icon: <ReactChatbotIcon />,
      icon: <ChatbotIcon />,
      path: '/react-chatbot',
    },
    {
      id: 'guided-chatbot',
      label: 'Chatbot Guiado',
      icon: <GuidedChatbotIcon />,
      path: '/guided-chatbot',
    },
  ];

  // Determinar qué sección debe estar abierta basándose en la ruta actual
  const getInitialOpenSections = useMemo(() => {
    const openSections: { [key: string]: boolean } = {};

    menuItems.forEach((item) => {
      if (item.subItems) {
        const hasActiveSubItem = item.subItems.some(
          (sub) => location.pathname === sub.path || location.pathname.startsWith(sub.path + '/')
        );
        if (hasActiveSubItem) {
          openSections[item.id] = true;
        }
      }
    });

    return openSections;
  }, [location.pathname]);

  const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>(getInitialOpenSections);

  // Actualizar las secciones abiertas cuando cambie la ruta
  useEffect(() => {
    const newOpenSections: { [key: string]: boolean } = {};

    menuItems.forEach((item) => {
      if (item.subItems) {
        const hasActiveSubItem = item.subItems.some(
          (sub) => location.pathname === sub.path || location.pathname.startsWith(sub.path + '/')
        );
        if (hasActiveSubItem) {
          newOpenSections[item.id] = true;
        }
      }
    });

    setOpenSections(newOpenSections);
  }, [location.pathname]);

  const handleToggleSection = (sectionId: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const isActive = (path?: string, subItems?: SubMenuItem[]) => {
    if (path) {
      return location.pathname === path;
    }
    if (subItems) {
      return subItems.some((sub) => location.pathname === sub.path);
    }
    return false;
  };

  const isSubItemActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: '#1e1e2e',
          color: '#ffffff',
          borderRight: 'none',
          top: '64px', // Altura del nav superior
        },
      }}
    >
      <div className="sidebar-content">
        <List>
          {menuItems.map((item) => (
            <React.Fragment key={item.id}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    if (item.subItems) {
                      handleToggleSection(item.id);
                    } else if (item.path) {
                      handleNavigate(item.path);
                    }
                  }}
                  className={isActive(item.path, item.subItems) ? 'active' : ''}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(102, 126, 234, 0.1)',
                    },
                    '&.active': {
                      backgroundColor: 'rgba(102, 126, 234, 0.2)',
                      borderLeft: '3px solid #667eea',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isActive(item.path, item.subItems) ? '#667eea' : '#a0a0b0',
                      minWidth: 40,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    sx={{
                      '& .MuiTypography-root': {
                        fontSize: '0.9rem',
                        fontWeight: isActive(item.path, item.subItems) ? 600 : 400,
                      },
                    }}
                  />
                  {item.subItems && (
                    <>
                      {openSections[item.id] ? (
                        <ExpandLess sx={{ color: '#a0a0b0' }} />
                      ) : (
                        <ExpandMore sx={{ color: '#a0a0b0' }} />
                      )}
                    </>
                  )}
                </ListItemButton>
              </ListItem>

              {item.subItems && (
                <Collapse in={openSections[item.id]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.subItems.map((subItem) => (
                      <ListItemButton
                        key={subItem.id}
                        onClick={() => handleNavigate(subItem.path)}
                        className={isSubItemActive(subItem.path) ? 'active-sub' : ''}
                        sx={{
                          pl: 4,
                          '&:hover': {
                            backgroundColor: 'rgba(102, 126, 234, 0.1)',
                          },
                          '&.active-sub': {
                            backgroundColor: 'rgba(102, 126, 234, 0.15)',
                            borderLeft: '3px solid #667eea',
                          },
                        }}
                      >
                        {subItem.icon && (
                          <ListItemIcon
                            sx={{
                              color: isSubItemActive(subItem.path) ? '#667eea' : '#a0a0b0',
                              minWidth: 36,
                            }}
                          >
                            {subItem.icon}
                          </ListItemIcon>
                        )}
                        <ListItemText
                          primary={subItem.label}
                          sx={{
                            '& .MuiTypography-root': {
                              fontSize: '0.85rem',
                              fontWeight: isSubItemActive(subItem.path) ? 600 : 400,
                            },
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              )}

              <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
            </React.Fragment>
          ))}
        </List>
      </div>
    </Drawer>
  );
};

export default Sidebar;
